// Fullscreen slideshow over a list of assets. Auto-advances through photos
// on a timer; videos autoplay and advance when they end. Reuses the same
// Bearer-authed blob-prefetch the viewer/thumbnails use (the resource routes
// require the Authorization header, so a raw <img>/<video> src can't reach
// them).
//
// Controls: ← / → prev-next, Space play/pause, Esc / ✕ close. The control bar
// auto-hides after a few idle seconds and reappears on pointer movement.

import { useCallback, useEffect, useRef, useState } from 'react';
import { prefetchAuthedImage } from '../../api/client';
import { pickDisplaySource, rotationStyle, useElementBox } from '../../lib/media';
import type { AssetDTO } from '../../api/types';

const PHOTO_INTERVAL_MS = 5000;

interface SlideshowProps {
  assets: AssetDTO[];
  startIndex?: number;
  onClose: () => void;
  /// Pagination hooks so the slideshow can pull the rest of the library as it
  /// advances, rather than looping over only the currently-loaded pages.
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}


export function Slideshow({
  assets,
  startIndex = 0,
  onClose,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: SlideshowProps) {
  const [index, setIndex] = useState(
    Math.min(Math.max(0, startIndex), Math.max(0, assets.length - 1)),
  );
  const [playing, setPlaying] = useState(true);
  const [pendingNext, setPendingNext] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [controlsVisible, setControlsVisible] = useState(true);

  const count = assets.length;
  const asset = assets[index];
  const isVideo = asset?.kind === 'video';
  // Display rotation is applied here as a CSS transform, same as the
  // lightbox; a quarter turn needs the stage's size to swap max-w/max-h.
  const [stageRef, stageBox] = useElementBox<HTMLDivElement>();
  const source = asset ? pickDisplaySource(asset) : null;
  const mediaStyle = rotationStyle(
    source?.rotation ?? asset?.display_rotation ?? 0,
    stageBox,
  );

  const next = useCallback(() => {
    setIndex((i) => {
      if (i + 1 < count) return i + 1;
      // At the end of what's loaded. If the library has more, pull it and
      // hold here — an effect advances once the new page lands. Only loop
      // back to the start once the entire library is loaded.
      if (hasNextPage) {
        fetchNextPage?.();
        setPendingNext(true);
        return i;
      }
      return 0;
    });
  }, [count, hasNextPage, fetchNextPage]);

  const prev = useCallback(
    () => setIndex((i) => (i - 1 + count) % count),
    [count],
  );

  // Pull pages ahead of the playhead so advancing is seamless.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && index >= count - 3) {
      fetchNextPage?.();
    }
  }, [index, count, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // When we held at the end waiting for more, advance as soon as it arrives.
  useEffect(() => {
    if (pendingNext && index + 1 < count) {
      setPendingNext(false);
      setIndex(index + 1);
    }
  }, [pendingNext, index, count]);

  // Load the current asset's bytes into a blob URL (auth header → no direct
  // src). Falls back to the large thumbnail on HEIC decode failure (photos).
  useEffect(() => {
    if (!asset) return;
    let cancelled = false;
    let created: string | null = null;
    setSrc(null);
    setUsingFallback(false);
    setError(null);

    // lib/media picks the rendition this browser can actually decode —
    // the transcoded twin for HEIC/RAW/HEVC, the original otherwise.
    const path =
      pickDisplaySource(asset)?.path ?? `/v1/assets/${asset.id}/thumbnail?size=1024`;
    void (async () => {
      try {
        const url = await prefetchAuthedImage(path);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setSrc(url);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Load failed.');
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [asset]);

  // Photo auto-advance timer. Videos advance via their `ended` event instead,
  // so they're not cut off mid-clip.
  useEffect(() => {
    if (!playing || isVideo || count <= 1) return;
    const t = setTimeout(next, PHOTO_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [playing, isVideo, index, count, next]);

  // Keyboard controls.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === ' ') {
        e.preventDefault();
        setPlaying((p) => !p);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, onClose]);

  // Auto-hide the control bar after idle; reveal on pointer movement.
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const poke = useCallback(() => {
    setControlsVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setControlsVisible(false), 2800);
  }, []);
  useEffect(() => {
    poke();
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [poke, index]);

  if (!asset) return null;

  const btn =
    'flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25';

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black"
      onMouseMove={poke}
      onClick={poke}
    >
      {/* top bar: counter + close */}
      <div
        className={
          'absolute left-0 right-0 top-0 z-10 flex items-center justify-between px-4 py-3 transition-opacity duration-300 ' +
          (controlsVisible ? 'opacity-100' : 'opacity-0')
        }
      >
        <span className="rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-neutral-200">
          {index + 1} / {count}
          {hasNextPage ? '+' : ''}
          {pendingNext && ' · loading more…'}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close slideshow"
          className={btn}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* stage */}
      <div ref={stageRef} className="flex min-h-0 flex-1 items-center justify-center p-4">
        {error ? (
          <div className="text-sm text-red-300">{error}</div>
        ) : !src ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : isVideo ? (
          <video
            key={asset.id}
            src={src}
            autoPlay
            controls
            playsInline
            onEnded={() => playing && next()}
            style={mediaStyle}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <img
            key={asset.id}
            src={src}
            alt={asset.caption ?? ''}
            onError={() => {
              if (usingFallback) {
                setError('Image failed to load.');
                return;
              }
              setUsingFallback(true);
              void (async () => {
                try {
                  const url = await prefetchAuthedImage(
                    `/v1/assets/${asset.id}/thumbnail?size=1024`,
                  );
                  setSrc((old) => {
                    if (old) URL.revokeObjectURL(old);
                    return url;
                  });
                } catch {
                  setError('Image failed to load.');
                }
              })();
            }}
            style={mediaStyle}
            className="max-h-full max-w-full object-contain"
          />
        )}
      </div>

      {/* bottom controls */}
      <div
        className={
          'absolute bottom-0 left-1/2 z-10 mb-5 flex -translate-x-1/2 items-center gap-4 rounded-full bg-black/50 px-5 py-2 backdrop-blur transition-opacity duration-300 ' +
          (controlsVisible ? 'opacity-100' : 'opacity-0')
        }
      >
        <button type="button" onClick={prev} aria-label="Previous" className={btn}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? 'Pause' : 'Play'}
          className={btn}
        >
          {playing ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <polygon points="6 4 20 12 6 20 6 4" />
            </svg>
          )}
        </button>
        <button type="button" onClick={next} aria-label="Next" className={btn}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
